/**
 * @file Domain types for the extraction pipeline: parsed-file chunks, chunk
 * labels and provenance, the per-chunk process document (column + row axes),
 * and the import proposal shape the agent emits for the user to confirm. Shared
 * between the web app, Firebase Functions, and the Cloud Run extraction tools so
 * every stage agrees on chunk identity and the process contract.
 */
import type { DocId, FieldId, RowClassification, RowClassificationTag, TimeType } from './System.types';
import type { AgentRecordStatus } from './AgentContext.types';
import type { EmailExtractedEntity } from './System.Agent.EmailAnalysis.types';
import type { EmailExtractionEntityConfig, FormEntityReferenceSlot } from './System.Agents.types';
import type { FormLevelFieldDescriptors } from './System.FormLevelFieldDescriptors.types';
import type { DateFieldType, DateLocale, NumberLocale } from './System.Agent.ExtractionCoercion.types';
import type { SourceCellSource, SourceCitation } from './System.Agent.PdfEvidence.types';

/** Short semantic identifier + description assigned to every parsed chunk. */
export type ChunkLabel = {
  /** lowerCamelCase semantic slug. Unique within a single parsed file. */
  id: string;
  /** Short human-readable description (≤8 words). */
  description: string;
  /**
   * Bucket key shared by all chunks that are the same logical thing in this
   * file (e.g. one table split across pages). For a standalone chunk,
   * `chunkGroupId === id`. For a multi-chunk group, group members share this
   * value while each retains a distinct `id`.
   */
  chunkGroupId: string;
};

/** Provenance: where a chunk came from in the source document. */
export type ChunkSource =
  | { kind: 'sheet'; index: number; name: string }
  | { kind: 'jsonNode'; path: string }
  | { kind: 'xmlNode'; path: string }
  | { kind: 'markdownSection'; ordinal: number; precedingHeading?: string }
  | {
      kind: 'pdfChunk';
      chunkId: string;
      page: number;
      box: { top: number; bottom: number; left: number; right: number };
    }
  | { kind: 'pdfPage'; page: number }
  | { kind: 'textFile' }
  | { kind: 'emailBody' }
  | { kind: 'emailAttachment'; filename: string; nested: ChunkSource }
  | {
      /**
       * A table the propose-time line-item synthesizer interpreted from prose in
       * `from` (an email body). NOT a verbatim parse — the rows are the model's
       * reading of a request expressed as text, carried with this provenance so
       * Mapping Review flags them ("Line items interpreted from …") for reviewer
       * scrutiny rather than presenting inferred rows as source-of-truth.
       */
      kind: 'synthesizedLineItems';
      from: ChunkSource;
    };

export type TableChunk = {
  kind: 'table';
  label: ChunkLabel;
  source: ChunkSource;
  sourceColumns: string[];
  rows: Record<string, string | number | null>[];
  sampleRows: Record<string, string | number | null>[];
  /** Row count from parse when `rows` is omitted (e.g. Firestore mapping payload). */
  parsedRowCount?: number;
};

export type TextChunk = {
  kind: 'text';
  label: ChunkLabel;
  source: ChunkSource;
  content: string;
};

export type Chunk = TableChunk | TextChunk;

/**
 * A reference to a unit of parsed content, used everywhere the pipeline names
 * what to read or import. The `scope` discriminator makes chunk-vs-group an
 * exclusive choice — `{ scope: 'chunk', id }` is one fragment (matched by
 * `label.id`), `{ scope: 'group', id }` is a whole logical section (every chunk
 * sharing that `chunkGroupId`, ordered). Because the scope is explicit, a chunk
 * id and a group id may coincide (a standalone chunk has `id === chunkGroupId`)
 * without ambiguity. Resolved to an ordered, single-kind `Chunk[]` by
 * `resolveSelection`.
 */
export type ChunkRef =
  | { scope: 'chunk'; id: string }
  | { scope: 'group'; id: string };

/** A chunk emitted by a parser before the labelling pass has run. */
export type UnlabelledTableChunk = Omit<TableChunk, 'label'>;
export type UnlabelledTextChunk = Omit<TextChunk, 'label'>;
export type UnlabelledChunk = UnlabelledTableChunk | UnlabelledTextChunk;

export type MappingSuggestion = {
  sourceColumn: string;
  targetTypeId: string | null;
  targetLabel: string | null;
  confidence: number;
};

export type ConfirmedMapping = {
  sourceColumn: string;
  targetTypeId: string | null;
};

export type ExtractionMappingData = {
  chunks: Chunk[];
  mappingSuggestions: MappingSuggestion[];
  formFieldMappingSuggestions?: MappingSuggestion[];
};

export type ExtractionStatus =
  | 'uploading'
  | 'parsing'
  | 'extracting'
  | 'mapping'
  | 'approved'
  | 'rejected'
  | 'failed';

export type ExtractionSourceTypeId = string;

/**
 * What a target field holds in lighting terms, so the verifier's lighting
 * checks (catalog grammar, allowed values, quantity, tag cross-check) run on
 * any tenant's form without knowing its field ids. `manufacturer` runs no
 * check of its own: it marks the reference column whose resolved records are
 * a schedule's `manufacturerIds`, the manufacturers whose published pricebook
 * editions the catalog grammar check parses against.
 */
export type ExtractionFieldRole = 'catalogNumber' | 'quantity' | 'cct' | 'voltage' | 'typeTag' | 'manufacturer';

export type ExtractionTargetField = {
  fieldId: FieldId;
  label: string;
  fieldDataTypeId: string;
  /** Set through `ExtractionSourceConfig.targetFields`; absent means no lighting check runs on the field. */
  role?: ExtractionFieldRole;
  /**
   * When set, this target is a table entity-reference column: extracted text is resolved to a record
   * or stored as manual entry (see `tableReferenceValueResolutions` on the extraction doc).
   */
  tableReferenceEntity?: { entityId: string; entityGroupId?: string };
};

export const ENTITY_NAME_TARGET: ExtractionTargetField = { fieldId: '__name__', label: 'Record Name', fieldDataTypeId: 'text' } as const;

export const EXTRACTION_ENTITY_REFERENCE_FIELD_DATA_TYPE_ID = 'entityReference' as const;

/** Normalized lookup key for a cell’s extracted text (trim + lowercase). */
export function tableReferenceExtractedKey(raw: string | number | null | undefined): string | null {
  if (raw == null) return null;
  const t = String(raw).trim();
  if (!t) return null;
  return t.toLowerCase();
}

export type TableReferenceValueResolution =
  | {
      kind: 'entity';
      docId: string;
      cacheLabel?: string;
      /**
       * Typesafe match probability in [0,1] that the record IS the extracted
       * text's entity. Absent on user-set resolutions — confidence only ever
       * describes a model judgment.
       */
      confidence?: number;
    }
  | { kind: 'manual'; text: string };

/**
 * Column-mapping confidence below this value is flagged: counted in
 * `ProcessDocument.summary.lowConfidence` server-side and highlighted in the
 * Mapping Review UI. One constant, shared by both ends.
 */
export const CONFIDENCE_WARN_THRESHOLD = 0.5;

/**
 * Column-mapping confidence at or above this value reads as trustworthy (the
 * green "high" band in the Mapping Review UI). Pairs with
 * `CONFIDENCE_WARN_THRESHOLD`: ≥ high → green, ≥ warn → orange, else red.
 */
export const CONFIDENCE_HIGH_THRESHOLD = 0.8;

/** Column typeId → normalized extracted key → resolution chosen in the mapping editor. */
export type TableReferenceValueResolutionsByColumn = Record<
  string,
  Record<string, TableReferenceValueResolution>
>;

/**
 * Cap on ranked options persisted per judged mapping decision: the winner plus
 * up to four runners-up from the Typesafe `choice` distribution. Shared by the
 * propose-time services (slice size), the entity-record search (`hitsPerPage`),
 * and the Mapping Review UI ("Top matches" tier size).
 */
export const MAPPING_ALTERNATIVES_TOP_K = 5;

/** A runner-up fact from a Typesafe `choice` distribution, by `facts` index. */
export interface FactAlternative {
  factIndex: number;
  /** Typesafe `choice` probability in [0,1] for this fact→target assignment. */
  probability: number;
}

/** A runner-up source column from a Typesafe `choice` distribution. */
export interface ColumnAlternative {
  sourceColumn: string;
  /** Typesafe `choice` probability in [0,1] for this column→field assignment. */
  probability: number;
}

/** A runner-up target table from a Typesafe `choice` distribution. */
export interface TableAlternative {
  tableId: string;
  /** Typesafe `choice` probability in [0,1] for this group→table assignment. */
  probability: number;
}

/** A Typesafe-judged entity-record candidate for one extracted reference value. */
export interface ReferenceCandidate {
  docId: string;
  name: string;
  /** Typesafe `choice` probability in [0,1] that this record IS the extracted text's entity. */
  probability: number;
}

/** How the source file is parsed. */
export type ExtractionParserType =
  | 'landingai_or_spreadsheet'  // PDF via LandingAI, XLSX/XLS/CSV, JSON
  | 'spreadsheet_xml';          // XLSX/XLS/CSV, JSON, XML

/** Where extracted rows are written on approval. */
export type ExtractionTargetType = 'entity' | 'tableType' | 'formCreation';

/** Config document stored in extractionSourceConfigs/{sourceTypeId}. */
export type ExtractionSourceConfig = {
  docId?: DocId;
  sourceTypeId: ExtractionSourceTypeId;
  label: string;
  /** When set, uploaded files must match one of these extensions (no leading dot, case-insensitive). */
  allowedExtensions?: string[];
  parserType: ExtractionParserType;
  targetType: ExtractionTargetType;
  // entity target:
  entityId?: string;
  groupByEntityId?: string;
  /**
   * The entity target's fields. For a document target (`formCreation`,
   * `tableType`) the bound schema supplies the fields, and an entry here whose
   * `fieldId` matches a schema field (header field or table column) contributes
   * only its `role`.
   */
  targetFields?: ExtractionTargetField[];
  // tableType target:
  tableId?: string;
  // formCreation target:
  formTypeId?: string;
  formFields?: FormLevelFieldDescriptors;
  entityReference?: EmailExtractionEntityConfig;
  /** Multiple form-level entity refs (from layout at first bridge). */
  entityReferences?: FormEntityReferenceSlot[];
  // prompts:
  mappingSystemPrompt?: string;
  mappingInstruction?: string;
};

/**
 * Per-chunk summary returned by the agentic extractFromFile tool. Excludes full
 * row contents and full text bodies for token economy; downstream tools resolve
 * the chunk by `id` (the label slug) when applying mappings or extracting rows.
 */
export type ReadFileDataChunkSummary = {
  /** label.id — unique within the file. */
  id: string;
  /** label.description — short human-readable description. */
  description: string;
  kind: 'table' | 'text';
  /** tables only */
  sourceColumns?: string[];
  /** tables only — capped at 20 rows. */
  sampleRows?: Record<string, string | number | null>[];
  /** tables only */
  totalRowCount?: number;
  /** text only — first ~200 characters. */
  contentPreview?: string;
};

/**
 * Trace-correlation payload threaded from the cloud_run agentic loop into the
 * `httpReadFileData` HTTP seam so the server-side Doc AI work emits `$ai_span`
 * events on the same PostHog trace as the agent. `parentSpanId` is the
 * `extractFromFile` tool span's id (`toolCall.id`), so server spans nest under
 * it. `distinctId` is intentionally absent — the function derives a verified
 * `actorUid` from the bearer token.
 */
export type ExtractionTelemetry = {
  traceId: string;
  parentSpanId: string;
  sessionId?: string;
};

export type ReadFileDataRequest = {
  /** `temp/<userId>/<fileFolderId>` — the per-file folder. */
  fileFolder: string;
  /** Trace correlation for server-side Doc AI spans; omitted when telemetry is off. */
  telemetry?: ExtractionTelemetry;
};

export type ReadFileDataParser = 'json' | 'xml' | 'spreadsheet' | 'text' | 'landingai' | 'documentai';

/**
 * HTTP-layer response from `httpReadFileData`. Internal to the server: the
 * executor reads `parser`/`displayName` to build the agent-facing
 * `ReadFileDataResponse`, but neither the path nor the parser is forwarded as
 * the agent's file handle — the agent addresses files by their `fileId`.
 */
export type ReadFileDataHttpResponse =
  | {
      /**
       * A large PDF was routed to async batch extraction. The endpoint is an
       * idempotent poll: calling it again while the batch runs re-checks the
       * operation instead of resubmitting, and returns `ready` once it lands.
       */
      status: 'processing';
      /** Per-file temp folder. The cooked chunks live at `pathForStage(fileFolder, 'parsed', { name: 'labeled' })`. */
      fileFolder: string;
      /** Display filename, read from the original blob's `customMetadata.displayName`. */
      displayName: string;
    }
  | {
      /** The parse completed and `chunks` / `labeled.json` are populated. */
      status: 'ready';
      /** Per-file temp folder. The cooked chunks live at `pathForStage(fileFolder, 'parsed', { name: 'labeled' })`. */
      fileFolder: string;
      /** Which parser produced the persisted payload. A document-AI parser also writes the raw `rawParseResult.json`. */
      parser: ReadFileDataParser;
      /** Display filename, read from the original blob's `customMetadata.displayName`. */
      displayName: string;
      chunks: ReadFileDataChunkSummary[];
    };

/**
 * Per-chunk semantic label surfaced to the agent. Mirrors the labels persisted
 * on `Chunk.label` by the upgrade-labels pipeline; flat catalog form keeps the
 * agent from re-scanning `chunks` for ids/descriptions.
 */
export type ReadFileDataLabel = {
  id: string;
  description: string;
  chunkGroupId: string;
  kind: 'table' | 'text';
};

/**
 * Agent-facing response. `fileId` is the durable per-file handle (surfaced to
 * the agent as `<file_id>`) that every file tool accepts; the server expands it
 * to `temp/<actorUid>/<fileId>` via `buildFileFolder` and builds stage paths
 * with `pathForStage`. No request-scoped pointer is allocated.
 */
export type ReadFileDataResponse = {
  /** Durable per-file handle (the upload id). Pass to every file tool, this turn or later. */
  fileId: string;
  /** Human-readable label for the file, defaulting to the source filename. */
  description: string;
  chunks: ReadFileDataChunkSummary[];
  /** Flat catalog of per-chunk labels (id, description, chunkGroupId, kind). */
  labels: ReadFileDataLabel[];
  /** Which parser produced the result (informational; the agent addresses files by `fileId`). */
  parser: ReadFileDataParser;
};

/**
 * One catalog entry per parsed chunk, returned by `readExtractedFile` when no
 * selection is given. Carries both addresses the agent can pass back —
 * `chunkId` (this fragment, `label.id`) and `chunkGroupId` (its whole section)
 * — so the chunk-vs-group choice is explicit. Plus a human `provenance` line
 * and the per-kind size (`totalRowCount` for tables, `charCount` for text).
 * `tagCounts` is present only when a `proposeImport` process document covers
 * this chunk's selection. Never carries row contents — the catalog is the
 * "never the raw full file" guarantee.
 */
export type ExtractedFileCatalogEntry = {
  /** label.id — unique within the file. Pass back as `chunkId` to read this one fragment. */
  chunkId: string;
  /** Pass back as `groupId` to read the whole section; fragments of one logical thing (e.g. a paged table) share it. */
  chunkGroupId: string;
  /** label.description — short human-readable summary. */
  description: string;
  kind: 'table' | 'text';
  /** Human-readable provenance, e.g. `Sheet "Parts" (#1)` or `PDF page 3`. */
  provenance: string;
  /** tables only */
  sourceColumns?: string[];
  /** tables only — parsed row count. */
  totalRowCount?: number;
  /** text only — character count of the content. */
  charCount?: number;
  /** Present when a process document exists for this chunk: rows per classification tag. */
  tagCounts?: Record<RowClassificationTag, number>;
};

/** Orient view: one entry per chunk, no row contents. */
export type ExtractedFileCatalog = {
  kind: 'catalog';
  fileFolder: string;
  chunks: ExtractedFileCatalogEntry[];
};

/**
 * Detail view for a table selection: a `[offset, offset+limit)` window over the
 * resolved selection's rows (one chunk, or the concatenated rows of a group),
 * optionally filtered to a single classification `tag`. `nextOffset` is set
 * only when more rows remain past the window. `ref` echoes the selection read.
 */
export type ExtractedFileTableWindow = {
  kind: 'table';
  /** The selection that was read. */
  ref: ChunkRef;
  /** label.ids of the chunks forming this selection, in order. */
  members: string[];
  columns: string[];
  rows: Record<string, string | number | null>[];
  returnedCount: number;
  /** Total rows in the selection (after any `tag` filter). */
  totalRowCount: number;
  /** The classification tag the window was filtered to, if any. */
  tag?: RowClassificationTag;
  nextOffset?: number;
};

/**
 * Detail view for a text selection: a `[offset, offset+limit)` character slice
 * over the resolved selection's content (one chunk, or the joined content of a
 * group). `ref` echoes the selection read.
 */
export type ExtractedFileTextWindow = {
  kind: 'text';
  ref: ChunkRef;
  members: string[];
  textSlice: string;
  returnedChars: number;
  totalChars: number;
  nextOffset?: number;
};

/**
 * Response of the `readExtractedFile` agent tool. No selection → `catalog`; a
 * `chunkId` or `groupId` resolves to a selection and the server dispatches on
 * its kind → `table` window or `text` window. Read-only; reads the durable
 * `parsed/labeled.json` (+ `process/<n>.json` for tags) and never re-parses.
 */
export type ReadExtractedFileResponse =
  | ExtractedFileCatalog
  | ExtractedFileTableWindow
  | ExtractedFileTextWindow;

export type ExtractionColumnMapping = {
  sourceColumn: string;
  targetField: string;
};

export type SearchEntityRecordsRequest = {
  entityId: string;
  names: string[];
  entityGroupId?: string;
};

/**
 * Keyed by the `tableReferenceExtractedKey`-normalized query. Each entry holds
 * up to `MAPPING_ALTERNATIVES_TOP_K` candidates in Algolia relevance order;
 * `[]` means the search returned nothing for that name.
 */
export type SearchEntityRecordsResponse = {
  results: Record<string, { docId: string; name: string }[]>;
};

/**
 * Agent-facing universal search over the unified Algolia index. One global tool
 * (`search`) searches every indexed kind, optionally narrowed by `documentType`
 * and parent. The agent-facing kind names map to index `_collectionName` facet
 * values: record→records, form→formVersions, tableRow→arTableRows,
 * attachment→attachments.
 *
 * Record narrowing (`entityId`/`entityName`, `entityGroupId`/`entityGroupName`,
 * `status`) is records-only: supplying any of them scopes the search to records.
 * Each id has a name alternative the endpoint resolves (`entityName` →
 * `entityId` via `description.shortLabel`; `entityGroupName` → `entityGroupId`
 * within the resolved entity's `recordGroups`); pass the id when known, the name
 * otherwise — never both. Results are gated per kind by the user's global read
 * right in the endpoint.
 */
export type AgentSearchDocumentType = 'record' | 'form' | 'tableRow' | 'attachment';

export type AgentSearchRequest = {
  query: string;
  documentType?: AgentSearchDocumentType;
  entityId?: string;
  entityName?: string;
  entityGroupId?: string;
  entityGroupName?: string;
  status?: AgentRecordStatus;
  limit?: number;
};

/** A single search hit, tagged by `kind`, carrying the ids needed to read or link it. */
export type AgentSearchHit =
  | { kind: 'record'; recordId: string; entityId: string; name: string; status?: AgentRecordStatus }
  | { kind: 'form'; formId: string; formVersionId: string; formNumber?: string; formType?: string; state?: string }
  | { kind: 'tableRow'; tableId: string; rowId: string; parentArtifactType?: 'form' | 'entity'; formId?: string; entityId?: string; title: string; subtitle?: string }
  | { kind: 'attachment'; attachmentId: string; title: string; subtitle?: string };

export type AgentSearchResponse = {
  hits: AgentSearchHit[];
};

export type PartsSource = 'algolia' | 'signify';

export interface PartCandidate {
  source: PartsSource;
  sourceId: string;
  name: string;
  sku?: string;
  summary?: string;
}

export interface PartResolutionRow {
  rowIndex: number;
  seed: { name?: string; sku?: string; description?: string };
  candidates: PartCandidate[];
  bestPick?: { sourceId: string; source: PartsSource; confidence: number };
  decision: 'reference' | 'manual';
  resolved:
    | { kind: 'reference'; docId: string; source: PartsSource; label?: string }
    | { kind: 'manual'; text: string };
}

export interface PartResolutionsDocument {
  version: 1;
  source: { fileFolder: string; ref: ChunkRef };
  seedMapping: { nameColumn?: string; skuColumn?: string; descriptionColumn?: string };
  rows: PartResolutionRow[];
  summary: {
    total: number;
    resolved: number;
    ambiguous: number;
    unresolved: number;
    signifyUsed: boolean;
  };
}

/**
 * The single bound document a whole-document staging targets — whichever the
 * page binds: a form version or an entity record. Injected from page context,
 * never agent-chosen.
 */
export type DocumentBinding =
  | { kind: 'formVersion'; formId: string; formVersionId: string }
  | { kind: 'record'; entityId: string; recordId: string };

/** Whole-document staging target: every content type the extraction contains. */
export type DocumentTarget = { kind: 'document'; binding: DocumentBinding };

/** Bulk staging target: each source row becomes one record of the entity. */
export type EntityRecordsTarget = { kind: 'entityRecords'; entityId: string; nameColumn?: string; maxRecords?: number };

export type MappingTarget = DocumentTarget | EntityRecordsTarget;

/**
 * Per-column normalization rule. Applied row-by-row by the bulk apply tools in
 * `ColumnPlan.rules` order before writing. Each rule is scoped to a single
 * source column. `trim`, `coerceNumber`, and `coerceDate` are auto-derived by
 * `proposeImport` (the latter two on numeric/date-typed columns respectively);
 * `lowercase`, `uppercase`, and `replace` are honored primitives the apply layer
 * supports but nothing currently emits. `coerceDate` carries its target
 * `dateType` because the generic rule engine has no field-type context; the
 * deriver stamps it and must restamp on a field-type change.
 */
export type CellCleaningRule =
  | { kind: 'trim' }
  | { kind: 'lowercase' }
  | { kind: 'uppercase' }
  | { kind: 'replace'; find: string; replaceWith: string }
  | { kind: 'coerceNumber' }
  | { kind: 'coerceDate'; dateType: DateFieldType };

/**
 * Where one localization axis came from, surfaced in Mapping Review so an
 * inferred guess reads differently from an explicit choice: `agent` (tool arg),
 * `user` (reviewer override), `orgDefault` (tenant parameter), `inferred`
 * (resolved structurally from column evidence — now explicit rather than an
 * absent value). Absent → the axis is unset.
 */
export type LocaleSource = 'agent' | 'user' | 'orgDefault' | 'inferred';

/**
 * Per-axis provenance for a staging's localization decisions, each an
 * independent `LocaleSource`: the numeric-format and date-order conventions,
 * the two-digit-year `centuryPivot`, and the `dateZone`. Any axis may be absent
 * when that axis was never decided. Encoding is a load-time fact, not a user
 * axis, so it carries no provenance.
 */
export type LocaleProvenance = {
  number?: LocaleSource;
  date?: LocaleSource;
  centuryPivot?: LocaleSource;
  dateZone?: LocaleSource;
};

/**
 * One source column's full plan: where it maps, how confident the mapping is,
 * how to clean it, and — for reference columns — the value→record resolution
 * per normalized cell key.
 */
export interface ColumnPlan {
  sourceColumn: string;
  targetField: string;
  /** Typesafe `choice` confidence in [0,1] for this column→field assignment. */
  confidence: number;
  /** Runner-up source columns for `targetField`, descending probability, ≤4. */
  alternatives: ColumnAlternative[];
  /** Cleaning rules applied in array order before write. May be empty. */
  rules: CellCleaningRule[];
  /**
   * Reference columns only: normalized extracted key → resolution (the inner
   * value of `TableReferenceValueResolutionsByColumn`). Absent for plain fields.
   */
  resolution?: Record<string, TableReferenceValueResolution>;
  /**
   * Reference columns only: normalized extracted key → ALL Typesafe-judged
   * record candidates (chosen included), descending probability, ≤5. Persists
   * even when the model resolved to manual so the review UI can rank overrides.
   */
  candidates?: Record<string, ReferenceCandidate[]>;
  /** This column's magnitude convention: values already `percent` (0–100) or `fraction` (0–1). */
  percentScale?: 'percent' | 'fraction';
  /** Column-consensus currency symbol/code detected across the column's cells at propose time. */
  detectedCurrency?: string;
}

/**
 * Explicit user decision for one source column, recorded in the Mapping Review
 * UI — a structural fact the apply tools act on. `remove` unmaps the column
 * (not written); `set` overrides a proposed mapping or adds one the model
 * missed.
 */
export type ProcessColumnDecision =
  | { kind: 'remove' }
  | {
      kind: 'set';
      targetField: string;
      /** Replaces the model-proposed rules wholesale when present. */
      rules?: CellCleaningRule[];
      /** Reviewer override of the column's magnitude convention; wins over `columns[].percentScale`. */
      percentScale?: 'percent' | 'fraction';
      /** Per normalized cell key; wins over `columns[].resolution` AND part resolutions. */
      resolutionOverrides?: Record<string, TableReferenceValueResolution>;
    };

/**
 * Per-table mapping edits recorded in the Mapping Review UI — the user-decided
 * overlay on one table's `columns`/`rows` axes. The Typesafe-judged axes are
 * never mutated; model judgments and user decisions stay separate. The apply
 * tools validate the overlay at execution time and act on it.
 */
export interface TableMappingDecisions {
  /** Indexes into `rows` the user rejected — skipped at write, index reserved. */
  rejectedRows: number[];
  /** Keyed by sourceColumn (the stable parsed axis). */
  columns: Record<string, ProcessColumnDecision>;
  /** Raw cell value overrides: rowIndex → sourceColumn → value. Applied BEFORE cleaning rules. */
  cellOverrides: Record<number, Record<string, string | number | null>>;
}

/**
 * Explicit user decision for one header field, keyed by `targetField`.
 * `remove` drops the proposed write; `set` overrides the value (or adds a
 * write the model missed). Raw text — cleaning rules still apply at write.
 */
export type ProcessFieldDecision =
  | { kind: 'remove' }
  | { kind: 'set'; value: string | number | null };

/**
 * Explicit user decision for one document reference slot, keyed by `slotKey`.
 * `remove` drops the proposed link; `set` pins the slot to a resolution the
 * user chose (or adds one the model missed). `entityId`/`entityGroupId` carry
 * the slot identity the write path needs to resolve the record — sourced from
 * the model's `ReferencePlan` for a proposed slot and from the user's pick for
 * an added one, so an added slot (absent from the plan) still writes.
 */
export type ProcessReferenceDecision =
  | { kind: 'remove' }
  | {
      kind: 'set';
      resolution: TableReferenceValueResolution;
      entityId: string;
      entityGroupId?: string;
    };

/**
 * Overrides the Typesafe group→table assignment for one staged table.
 * `remove` keeps the group out of the apply; `set` sends it to a different
 * target table — the model's column plans no longer apply there, so only the
 * overlay's `columns` decisions are honored after a retarget.
 */
export type ProcessTableTargetDecision =
  | { kind: 'remove' }
  | { kind: 'set'; tableId: string };

/** Mapping edits for one staged table, keyed by its group id (`TablePlan.ref.id`). */
export interface ProcessTableDecisions extends TableMappingDecisions {
  target?: ProcessTableTargetDecision;
}

/** User overlay on a `ProcessImportDocument` (entity bulk import). */
export interface ProcessImportDecisions extends TableMappingDecisions {
  version: 1;
}

/** User overlay on a `ProcessWholeDocument`. */
export interface ProcessDocumentDecisions {
  version: 1;
  /** Keyed by targetField (field id on the bound document). */
  fields: Record<string, ProcessFieldDecision>;
  /** Keyed by slotKey (`entityGroupId` when grouped, else `entityId`). */
  references: Record<string, ProcessReferenceDecision>;
  /** Keyed by table group id (`TablePlan.ref.id`). */
  tables: Record<string, ProcessTableDecisions>;
}

/** A `ProcessWholeDocument`'s overlay before its first decision; spread, never mutated. */
export const EMPTY_PROCESS_DOCUMENT_DECISIONS: ProcessDocumentDecisions = { version: 1, fields: {}, references: {}, tables: {} };

/** One staged table's decisions before its first; spread, never mutated. */
export const EMPTY_PROCESS_TABLE_DECISIONS: ProcessTableDecisions = { rejectedRows: [], columns: {}, cellOverrides: {} };

/**
 * User-decided overlay on a `ProcessDocument`, written ONLY by the Mapping
 * Review client before approval. Shape follows the staging kind.
 */
export type ProcessUserDecisions = ProcessImportDecisions | ProcessDocumentDecisions;

/**
 * One extracted candidate fact: a verbatim label/value pair read from the
 * file's text chunks by the propose-time candidate pass (Haiku). Facts are the
 * option universe Typesafe maps header fields and references against, and are
 * persisted so the Mapping Review UI can remap without re-reading the file.
 */
export interface ExtractedFact {
  /** Verbatim label text from the document (e.g. "PO Number"). */
  label: string;
  /** Verbatim value text from the document (e.g. "4711"). */
  value: string;
  /** `label.id` of the text chunk the fact was read from. */
  chunkId: string;
  /**
   * `sourceCells` stagings only: the words the value was read from. The apply
   * stamps it on `meta.source` of the header field the fact lands on, unless
   * the reviewer overrode that field's value.
   */
  citation?: SourceCitation;
}

/**
 * One header field's plan: which extracted fact lands on which target field,
 * with Typesafe's confidence and the cleaning rules from the field's type.
 */
export interface FieldPlan {
  /** Field id on the bound document. */
  targetField: string;
  /** Index into `facts` of the candidate Typesafe chose. */
  factIndex: number;
  /** Typesafe `choice` confidence in [0,1] for this fact→field assignment. */
  confidence: number;
  /** Runner-up facts (winner excluded), descending probability, ≤4. */
  alternatives: FactAlternative[];
  /** Cleaning rules applied in array order before write. May be empty. */
  rules: CellCleaningRule[];
}

/**
 * One document reference slot's plan: the extracted fact Typesafe chose for
 * the slot, plus the value→record resolution (search + Typesafe match — the
 * same machinery as table reference columns).
 */
export interface ReferencePlan {
  /** Slot key on the bound document: `entityGroupId` when grouped, else `entityId`. */
  slotKey: string;
  entityId: string;
  entityGroupId?: string;
  /** Index into `facts` of the candidate Typesafe chose. */
  factIndex: number;
  /** Typesafe `choice` confidence in [0,1] for this fact→slot assignment. */
  confidence: number;
  /** Runner-up facts (winner excluded), descending probability, ≤4. */
  alternatives: FactAlternative[];
  /** Value→record resolution of the chosen fact's value. */
  resolution: TableReferenceValueResolution;
  /**
   * ALL Typesafe-judged record candidates for the chosen fact's value (chosen
   * included), descending probability, ≤5. Persists even when the model
   * resolved to manual so the review UI can rank overrides.
   */
  candidates: ReferenceCandidate[];
}

/** Per-table mapping summary (one table's `columns`/`rows` axes). */
export interface TableMappingSummary {
  mapped: number;
  referenceColumns: number;
  referenceValuesResolved: number;
  referenceValuesUnresolved: number;
  /** Source columns present in the parsed chunk that no target field claimed (dropped on apply). */
  unmappedSourceColumns: string[];
  rowsTotal: number;
  /** Count of rows per Typesafe classification tag. */
  tagCounts: Record<RowClassificationTag, number>;
  /** Columns whose mapping confidence fell below the warn threshold. */
  lowConfidence: number;
}

/**
 * One staged table inside a whole-document staging: a source table group,
 * the target table Typesafe assigned it to, and the same two Typesafe axes
 * (`columns`, `rows`) a bulk import stages.
 */
export interface TablePlan {
  /** Source selection: the table chunk group this plan covers. */
  ref: ChunkRef;
  /** Target table (table type id) on the bound document, Typesafe-assigned. */
  tableId: string;
  /** Typesafe `choice` confidence in [0,1] for this group→table assignment. */
  confidence: number;
  /** Runner-up target tables (winner excluded), descending probability, ≤4. */
  alternatives: TableAlternative[];
  columns: ColumnPlan[];
  rows: RowClassification[];
  /**
   * Target fields Typesafe judged but assigned no winning source column,
   * keyed by target field id → ranked source columns (descending probability,
   * ≤4). Feeds the unmapped-card and add-mapping rankings in the review UI.
   */
  unmappedTargets: Record<string, ColumnAlternative[]>;
  summary: TableMappingSummary;
}

/**
 * Bulk-import staging artifact for an entity collection, persisted at
 * `<fileFolder>/process/<n>.json`. Two symmetric axes, both model-driven
 * (Typesafe): `columns` (per source column, one `choice` per target field) and
 * `rows` (per source row, one `choice` tagging it value/summary/junk).
 * Index-aligned to the parsed chunk's `rows`. At apply the writer skips `junk`
 * rows and writes `value`/`summary`, each carrying its tag (`Meta.importRow`)
 * after the per-column cleaning rules; the other skip is an explicit user
 * rejection recorded in `decisions`, skipped at write with its index reserved.
 */
export interface ProcessImportDocument {
  version: 1;
  /**
   * `sourceCells` is present when the staging read the PDF intake extractor's
   * proven cells instead of the labeled chunks; `ref` then names one of its tables.
   */
  source: { fileFolder: string; ref: ChunkRef; sourceCells?: SourceCellSource };
  target: EntityRecordsTarget;
  columns: ColumnPlan[];
  rows: RowClassification[];
  /**
   * Target fields Typesafe judged but assigned no winning source column,
   * keyed by target field id → ranked source columns (descending probability,
   * ≤4). Feeds the unmapped-card and add-mapping rankings in the review UI.
   */
  unmappedTargets: Record<string, ColumnAlternative[]>;
  /** Present once the user has edited the proposal in the Mapping Review UI. */
  decisions?: ProcessImportDecisions;
  /**
   * Numeric-format convention supplied at propose time (agent or user).
   * Consumed by every numeric coercion of this staging — the apply writers and
   * the review preview. Absent → structural best guess.
   */
  locale?: NumberLocale;
  /**
   * Date-order convention for this staging, resolved at propose time (agent arg →
   * whole-column evidence via `inferDateLocale`). Consumed by every date coercion
   * — the apply writers and the preview. Absent → only unambiguous cells coerce.
   */
  dateLocale?: DateLocale;
  /**
   * Two-digit-year century pivot for this staging (reviewer or org default),
   * threaded into every date coercion; absent → the fixed built-in pivot.
   */
  centuryPivot?: number;
  /**
   * Fixed UTC offset (`±HH:MM`/`Z`) applied to zone-less `dateTime` cells
   * (reviewer or org default); `'naive'`/absent interprets them as UTC. Threaded
   * into every date coercion. No IANA/DST resolution — deterministic offset only.
   */
  dateZone?: string;
  /** Per-axis provenance of this staging's localization decisions (see `LocaleProvenance`). */
  localeSource?: LocaleProvenance;
  /**
   * Text-encoding metadata only — the decoded text itself lives in the parsed
   * artifact, not here. `chosen` is the encoding label applied (e.g. 'utf-8',
   * 'windows-1252'); `failed` marks a blocked decode; `candidates` are the
   * detected options, retained for audit and surfaced read-only in Mapping
   * Review (interactive re-decode is deferred to a follow-up).
   */
  encoding?: { chosen?: string; failed?: true; candidates?: string[] };
  summary: TableMappingSummary;
}

/** Document-level mapping summary for a whole-document staging. */
export interface DocumentMappingSummary {
  factsExtracted: number;
  fieldsMapped: number;
  /** Fields whose mapping confidence fell below the warn threshold. */
  fieldsLowConfidence: number;
  referencesMapped: number;
  referencesResolved: number;
  referencesUnresolved: number;
  tablesAssigned: number;
  /** Table group ids Typesafe assigned to no target table (not imported). */
  unassignedTableGroups: string[];
}

/**
 * Whole-document staging artifact, persisted at `<fileFolder>/process/<n>.json`.
 * Covers EVERY content type the extraction contains — header fields, document
 * references, and all table groups — so a partial apply of the bound document
 * is structurally impossible. All mapping and scoring is Typesafe; the
 * candidate facts come from the propose-time Haiku pass. `applyToDocument`
 * consumes this artifact; the agent never authors values, mappings, or scores.
 */
export interface ProcessWholeDocument {
  version: 1;
  /** `sourceCells` is present when the staging read the PDF intake extractor's proven cells instead of the labeled chunks. */
  source: { fileFolder: string; sourceCells?: SourceCellSource };
  target: DocumentTarget;
  facts: ExtractedFact[];
  fields: FieldPlan[];
  references: ReferencePlan[];
  tables: TablePlan[];
  /**
   * Header fields Typesafe judged but assigned no winning fact, keyed by
   * target field id → ranked facts (descending probability, ≤4). Feeds the
   * add-field ranking in the review UI.
   */
  unmappedFields: Record<string, FactAlternative[]>;
  /**
   * Reference slots Typesafe judged but assigned no winning fact, keyed by
   * slot key → ranked facts (descending probability, ≤4). Feeds the
   * add-reference ranking in the review UI.
   */
  unmappedReferences: Record<string, FactAlternative[]>;
  /** Present once the user has edited the proposal in the Mapping Review UI. */
  decisions?: ProcessDocumentDecisions;
  /**
   * Numeric-format convention supplied at propose time (agent or user).
   * Consumed by every numeric coercion of this staging — the apply writers and
   * the review preview. Absent → structural best guess.
   */
  locale?: NumberLocale;
  /**
   * Date-order convention for this staging, resolved at propose time (agent arg →
   * whole-column evidence via `inferDateLocale`). Consumed by every date coercion
   * — the apply writers and the preview. Absent → only unambiguous cells coerce.
   */
  dateLocale?: DateLocale;
  /**
   * Two-digit-year century pivot for this staging (reviewer or org default),
   * threaded into every date coercion; absent → the fixed built-in pivot.
   */
  centuryPivot?: number;
  /**
   * Fixed UTC offset (`±HH:MM`/`Z`) applied to zone-less `dateTime` cells
   * (reviewer or org default); `'naive'`/absent interprets them as UTC. Threaded
   * into every date coercion. No IANA/DST resolution — deterministic offset only.
   */
  dateZone?: string;
  /** Per-axis provenance of this staging's localization decisions (see `LocaleProvenance`). */
  localeSource?: LocaleProvenance;
  /**
   * Text-encoding metadata only — the decoded text itself lives in the parsed
   * artifact, not here. `chosen` is the encoding label applied (e.g. 'utf-8',
   * 'windows-1252'); `failed` marks a blocked decode; `candidates` are the
   * detected options, retained for audit and surfaced read-only in Mapping
   * Review (interactive re-decode is deferred to a follow-up).
   */
  encoding?: { chosen?: string; failed?: true; candidates?: string[] };
  summary: DocumentMappingSummary;
}

/**
 * Single staging artifact at `<fileFolder>/process/<n>.json` — discriminated
 * by `target.kind`: a whole-document staging for the bound form version or
 * record, or a bulk import staging for an entity collection.
 */
export type ProcessDocument = ProcessWholeDocument | ProcessImportDocument;

/**
 * Response shape of the `proposeImport` agent tool. Addresses the single
 * `<fileFolder>/process/<n>.json` artifact via `pathForStage`. `columns` is
 * present on entity bulk stagings only and surfaces per-column confidence so
 * the agent can flag weak mappings to the user before applying; document
 * stagings return counts in `summary` — the Mapping Review UI is the review
 * surface, not the agent.
 */
export interface ProposeImportResponse {
  processIndex: number;
  summary: string;
  columns?: { sourceColumn: string; targetField: string; confidence: number }[];
}

export type Extraction = {
  docId?: DocId;
  userId: string;
  timestamp: TimeType;
  filename: string;
  /**
   * `temp/<userId>/<fileFolderId>` — the per-file folder. Absent when the
   * extraction has no source-file primary attachment (e.g. an email body
   * extraction with no attachments).
   */
  fileFolder?: string;
  fileContentType: string;
  fileSizeBytes: number;
  status: ExtractionStatus;
  error?: string;
  sourceTypeId: ExtractionSourceTypeId;
  mappingData?: ExtractionMappingData;
  rowsStoragePath?: string;
  /** GCS path to the full raw provider parse JSON (`extractions/{id}/rawParseResult.json`). */
  rawParseResultStoragePath?: string;
  confirmedMappings?: ConfirmedMapping[];
  totalRowCount?: number;
  skippedRowCount?: number;
  usage?: {
    llmInputTokens?: number;
    llmOutputTokens?: number;
    llmModel?: string;
    llmDurationMs?: number;
    parsePages?: number;
    parseCredits?: number;
  };
  // entity target fields:
  parsedMarkdown?: string;
  entityId?: string;
  groupByEntityId?: string;
  groupByRecordId?: string;
  createdRecordCount?: number;
  /** Entity record doc IDs created on approve (entity target). */
  createdRecordIds?: string[];
  manufacturer?: string;
  // tableType + formCreation target fields:
  formId?: string;
  formVersionId?: string;
  tableId?: string;
  createdRowCount?: number;
  // formCreation target fields:
  formTypeId?: string;
  analysisId?: string;
  formLevelFields?: Record<string, unknown>;
  extractedEntity?: EmailExtractedEntity;
  extractedEntities?: Record<string, EmailExtractedEntity>;
  entityReferenceSlots?: FormEntityReferenceSlot[];
  /** Per table reference column: normalized extracted text → entity or manual (mapping editor). */
  tableReferenceValueResolutions?: TableReferenceValueResolutionsByColumn;
};
